import React, { useState, useMemo, useEffect, useRef } from 'react';

/**
 * Format a day number with ordinal suffix (e.g. 11 -> 11th, 1 -> 1st, 2 -> 2nd, 3 -> 3rd)
 */
export const getOrdinalDate = (dateObj) => {
  const day = dateObj.getDate();
  const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
  const s = ['th', 'st', 'nd', 'rd'];
  const v = day % 100;
  const suffix = s[(v - 20) % 10] || s[v] || s[0];
  return `${day}${suffix} ${month}`;
};

/**
 * Format 24hr "HH:MM" into 12hr "hh:mm AM/PM"
 */
export const format12Hour = (timeStr) => {
  if (!timeStr) return '';
  const parts = timeStr.split(':');
  let h = parseInt(parts[0], 10);
  const m = parts[1] || '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  h = h ? h : 12;
  return `${h}:${m} ${ampm}`;
};

/**
 * Categorize a slot into a day period
 */
export const getSlotPeriod = (timeStr) => {
  if (!timeStr) return 'NOON';
  const parts = timeStr.split(':');
  const h = parseInt(parts[0], 10);
  if (h < 12) return 'MORNING';
  if (h < 17) return 'NOON';
  if (h < 20) return 'EVENING';
  return 'NIGHT';
};

export const PERIOD_DEFINITIONS = [
  { key: 'MORNING', title: 'MORNING', timeRange: '08:00 AM - 12:00 PM' },
  { key: 'NOON', title: 'NOON', timeRange: '12:00 PM - 05:00 PM' },
  { key: 'EVENING', title: 'EVENING', timeRange: '05:00 PM - 08:00 PM' },
  { key: 'NIGHT', title: 'NIGHT', timeRange: '08:00 PM - 11:00 PM' },
];

/**
 * SlotAvailabilityPicker
 * 
 * Props:
 * - slots: Array of { _id, date: 'YYYY-MM-DD', startTime: 'HH:MM', endTime: 'HH:MM', isBooked: Boolean }
 * - selectedSlot: currently selected slot object or { date, startTime }
 * - onSelectSlot: callback when a slot is chosen (slot) => void
 * - isTherapistManager: if true, shows delete buttons or manager tools
 * - onDeleteSlot: callback (slotId) => void
 * - daysToShow: number of days in window (default 14)
 * - cardsPerView: number of day cards visible at once (default 3)
 */
const SlotAvailabilityPicker = ({
  slots = [],
  weeklySchedule = null,
  consultationDuration = 50,
  durationFee = null,
  consultationFee = 999,
  selectedSlot = null,
  onSelectSlot,
  isTherapistManager = false,
  onDeleteSlot,
  daysToShow = 14,
  cardsPerView = 3,
  title = "Check available slots",
  embedded = false,
}) => {
  const dateInputRef = useRef(null);

  // Generate date range starting today or earliest slot date
  const dateList = useMemo(() => {
    const list = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    for (let i = 0; i < daysToShow; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const dayName = d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
      const formattedDate = getOrdinalDate(d);

      // Count active slots on this date
      let dateSlots = (slots || []).filter((s) => s.date === dateStr && (!s.isBooked || isTherapistManager));

      // If not in manager mode and entire slots array is empty, fallback to weeklySchedule configuration
      if (!isTherapistManager && (!slots || slots.length === 0) && weeklySchedule && typeof weeklySchedule === 'object') {
        const dayKey = dayNames[d.getDay()];
        const dayCfg = weeklySchedule[dayKey];
        if (dayCfg && dayCfg.enabled && dayCfg.startTime) {
          const start = dayCfg.startTime;
          const parts = start.split(':');
          const h = parseInt(parts[0], 10) || 0;
          const m = parseInt(parts[1], 10) || 0;
          const total = h * 60 + m + (Number(consultationDuration) || 50);
          const endH = Math.floor((total % (24 * 60)) / 60);
          const endM = total % 60;
          const end = dayCfg.endTime || `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;

          dateSlots = [
            {
              _id: `ws_${dateStr}_${start.replace(':', '')}`,
              date: dateStr,
              startTime: start,
              endTime: end,
              isBooked: false,
              isFromWeeklySchedule: true,
            },
          ];
        }
      }

      const availableCount = dateSlots.filter((s) => !s.isBooked).length;

      list.push({
        dateStr,
        dayName,
        formattedDate,
        dateObj: d,
        slots: dateSlots,
        availableCount,
        hasAvailable: availableCount > 0,
      });
    }
    return list;
  }, [slots, weeklySchedule, consultationDuration, daysToShow, isTherapistManager]);

  // Initial selected date (first date with slots or today)
  const [selectedDateStr, setSelectedDateStr] = useState(() => {
    if (selectedSlot?.date) return selectedSlot.date;
    const firstWithSlots = dateList.find((d) => d.hasAvailable);
    return firstWithSlots ? firstWithSlots.dateStr : dateList[0]?.dateStr || '';
  });

  // Start index for 3-card sliding carousel
  const [startIndex, setStartIndex] = useState(0);

  // Sync when selectedSlot prop changes or when slots load
  useEffect(() => {
    if (selectedSlot?.date) {
      setSelectedDateStr(selectedSlot.date);
      // Ensure the selected date is visible in carousel
      const idx = dateList.findIndex((d) => d.dateStr === selectedSlot.date);
      if (idx !== -1) {
        if (idx < startIndex || idx >= startIndex + cardsPerView) {
          setStartIndex(Math.max(0, Math.min(idx, Math.max(0, dateList.length - cardsPerView))));
        }
      }
    } else {
      // If current selectedDateStr has no slots, but another date does, switch to first date with available slots
      const currentItem = dateList.find((d) => d.dateStr === selectedDateStr);
      if (!currentItem || !currentItem.hasAvailable) {
        const firstWithSlots = dateList.find((d) => d.hasAvailable);
        if (firstWithSlots) {
          setSelectedDateStr(firstWithSlots.dateStr);
          const idx = dateList.findIndex((d) => d.dateStr === firstWithSlots.dateStr);
          if (idx !== -1) {
            setStartIndex(Math.max(0, Math.min(idx, Math.max(0, dateList.length - cardsPerView))));
          }
        }
      }
    }
  }, [slots, selectedSlot, dateList, cardsPerView]);

  const handlePrev = () => {
    setStartIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setStartIndex((prev) => Math.min(dateList.length - cardsPerView, prev + 1));
  };

  const handleDateClick = (dateItem) => {
    setSelectedDateStr(dateItem.dateStr);
  };

  const handleCalendarPick = (e) => {
    if (e.target.value) {
      const pickedStr = e.target.value;
      setSelectedDateStr(pickedStr);
      const idx = dateList.findIndex((d) => d.dateStr === pickedStr);
      if (idx !== -1) {
        setStartIndex(Math.max(0, Math.min(idx, dateList.length - cardsPerView)));
      }
    }
  };

  // Active date object
  const activeDateItem = dateList.find((d) => d.dateStr === selectedDateStr) || {
    dateStr: selectedDateStr,
    slots: slots.filter((s) => s.date === selectedDateStr),
    formattedDate: selectedDateStr,
  };

  // Group active date slots by period
  const periodSlots = useMemo(() => {
    const activeSlots = activeDateItem.slots || [];
    const grouped = {
      MORNING: [],
      NOON: [],
      EVENING: [],
      NIGHT: [],
    };

    activeSlots.forEach((slot) => {
      const period = getSlotPeriod(slot.startTime);
      if (grouped[period]) {
        grouped[period].push(slot);
      } else {
        grouped.NOON.push(slot);
      }
    });

    // Sort slots by time within each period
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
    });

    return grouped;
  }, [activeDateItem]);

  const visibleDates = dateList.slice(startIndex, startIndex + cardsPerView);

  return (
    <div style={{
      background: embedded ? 'transparent' : '#ffffff',
      borderRadius: embedded ? '0' : '20px',
      padding: embedded ? '0' : '24px',
      boxShadow: embedded ? 'none' : '0 4px 25px rgba(0, 0, 0, 0.04)',
      border: embedded ? 'none' : '1px solid #f1f5f9',
      fontFamily: "'Quicksand', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <h3 style={{
            margin: 0,
            fontSize: embedded ? '16px' : '20px',
            fontWeight: 800,
            color: '#0f172a',
            letterSpacing: '-0.2px'
          }}>
            {title}
          </h3>
          <span style={{
            fontSize: '11.5px',
            fontWeight: 700,
            color: '#0369a1',
            background: '#e0f2fe',
            padding: '2px 8px',
            borderRadius: '6px'
          }}>
            ⏱️ {consultationDuration} Mins {durationFee ? `• ₹${durationFee}` : ''}
          </span>
        </div>

        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => dateInputRef.current && dateInputRef.current.showPicker ? dateInputRef.current.showPicker() : dateInputRef.current.focus()}
            title="Pick a specific date"
            style={{
              padding: '6px 12px',
              borderRadius: '20px',
              border: '1.5px solid #e2e8f0',
              background: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              color: '#334155',
              fontSize: '12px',
              fontWeight: 700,
              transition: 'all 0.2s ease',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <span>Calendar</span>
          </button>
          <input
            ref={dateInputRef}
            type="date"
            onChange={handleCalendarPick}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              opacity: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none'
            }}
          />
        </div>
      </div>

      {/* Date Carousel */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '20px'
      }}>
        {/* Prev Arrow */}
        <button
          type="button"
          onClick={handlePrev}
          disabled={startIndex === 0}
          aria-label="Previous dates"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            border: '1.5px solid #e2e8f0',
            background: startIndex === 0 ? '#f8fafc' : '#ffffff',
            color: startIndex === 0 ? '#cbd5e1' : '#1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: startIndex === 0 ? 'not-allowed' : 'pointer',
            fontSize: '15px',
            fontWeight: 800,
            flexShrink: 0,
            transition: 'all 0.2s'
          }}
        >
          ‹
        </button>

        {/* Date Cards Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cardsPerView}, 1fr)`,
          gap: '8px',
          flex: 1,
          minWidth: 0
        }}>
          {visibleDates.map((dateItem) => {
            const isSelected = dateItem.dateStr === selectedDateStr;
            return (
              <div
                key={dateItem.dateStr}
                onClick={() => handleDateClick(dateItem)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  minWidth: 0
                }}
              >
                {/* Day Header (FRI, SAT, SUN) */}
                <div style={{
                  fontSize: '11px',
                  fontWeight: 800,
                  color: isSelected ? '#1e293b' : '#64748b',
                  marginBottom: '4px',
                  letterSpacing: '0.5px'
                }}>
                  {dateItem.dayName}
                </div>

                {/* Card Container */}
                <div style={{
                  width: '100%',
                  padding: '10px 6px',
                  borderRadius: '10px',
                  textAlign: 'center',
                  background: isSelected ? '#193f2a' : '#f8fafc',
                  border: isSelected ? '1.5px solid #193f2a' : '1.5px solid #e2e8f0',
                  color: isSelected ? '#ffffff' : '#1e293b',
                  boxShadow: isSelected ? '0 4px 12px rgba(25, 63, 42, 0.2)' : 'none',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}>
                  <div style={{
                    fontSize: '13.5px',
                    fontWeight: 800,
                    lineHeight: 1.2,
                    marginBottom: '3px',
                    color: isSelected ? '#ffffff' : '#0f172a',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {dateItem.formattedDate}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: isSelected
                      ? '#a7f3d0'
                      : dateItem.hasAvailable
                      ? '#059669'
                      : '#94a3b8',
                    whiteSpace: 'nowrap'
                  }}>
                    {dateItem.hasAvailable ? `${dateItem.availableCount} slots` : 'No slots'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Next Arrow */}
        <button
          type="button"
          onClick={handleNext}
          disabled={startIndex + cardsPerView >= dateList.length}
          aria-label="Next dates"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            border: '1.5px solid #e2e8f0',
            background: startIndex + cardsPerView >= dateList.length ? '#f8fafc' : '#ffffff',
            color: startIndex + cardsPerView >= dateList.length ? '#cbd5e1' : '#1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: startIndex + cardsPerView >= dateList.length ? 'not-allowed' : 'pointer',
            fontSize: '15px',
            fontWeight: 800,
            flexShrink: 0,
            transition: 'all 0.2s'
          }}
        >
          ›
        </button>
      </div>

      {/* Time Slots Categorized by Period */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {PERIOD_DEFINITIONS.map((def) => {
          const group = periodSlots[def.key] || [];
          if (group.length === 0) return null;

          return (
            <div key={def.key} style={{ background: '#f8fafc', padding: '12px 14px', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
              {/* Period Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '10px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{
                    fontSize: '12.5px',
                    fontWeight: 800,
                    color: '#0f172a',
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase'
                  }}>
                    {def.title}
                  </span>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    background: '#e2e8f0',
                    color: '#475569',
                    padding: '1px 6px',
                    borderRadius: '10px'
                  }}>
                    {group.length}
                  </span>
                </div>
                <span style={{
                  fontSize: '11.5px',
                  fontWeight: 600,
                  color: '#64748b'
                }}>
                  {def.timeRange}
                </span>
              </div>

              {/* Slot Buttons Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(92px, 1fr))',
                gap: '8px'
              }}>
                {group.map((slot) => {
                  const isSelected =
                    (selectedSlot?._id && selectedSlot._id === slot._id) ||
                    (selectedSlot?.date === slot.date && selectedSlot?.startTime === slot.startTime);
                  const isBooked = slot.isBooked;

                  return (
                    <div key={slot._id || `${slot.date}-${slot.startTime}`} style={{ position: 'relative' }}>
                      <button
                        type="button"
                        disabled={isBooked && !isTherapistManager}
                        onClick={() => onSelectSlot && onSelectSlot(slot)}
                        style={{
                          width: '100%',
                          padding: '8px 6px',
                          borderRadius: '8px',
                          border: isSelected ? '1.5px solid #193f2a' : '1.5px solid #e2e8f0',
                          background: isSelected
                            ? '#193f2a'
                            : isBooked
                            ? '#fef2f2'
                            : '#ffffff',
                          color: isSelected
                            ? '#ffffff'
                            : isBooked
                            ? '#991b1b'
                            : '#1e293b',
                          fontSize: '13px',
                          fontWeight: isSelected ? 800 : 700,
                          cursor: isBooked && !isTherapistManager ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          boxShadow: isSelected
                            ? '0 3px 8px rgba(25, 63, 42, 0.25)'
                            : '0 1px 2px rgba(0,0,0,0.03)',
                          transition: 'all 0.15s ease',
                          opacity: isBooked && !isTherapistManager ? 0.6 : 1,
                          boxSizing: 'border-box'
                        }}
                      >
                        <span>{format12Hour(slot.startTime)}</span>
                        {isBooked && (
                          <span style={{
                            fontSize: '9px',
                            fontWeight: 800,
                            background: '#fee2e2',
                            color: '#dc2626',
                            padding: '1px 3px',
                            borderRadius: '3px'
                          }}>
                            Booked
                          </span>
                        )}
                      </button>

                      {/* Delete button in Therapist Slot Manager */}
                      {isTherapistManager && onDeleteSlot && !isBooked && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onDeleteSlot(slot._id || slot, slot);
                          }}
                          title="Delete slot"
                          style={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            background: '#ef4444',
                            color: '#ffffff',
                            border: '1.5px solid #ffffff',
                            fontSize: '10px',
                            fontWeight: 900,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(239, 68, 68, 0.4)',
                            zIndex: 10,
                            transition: 'transform 0.15s ease'
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* If no slots in any period on the selected date */}
        {Object.values(periodSlots).every((group) => group.length === 0) && (
          <div style={{
            padding: '24px 16px',
            textAlign: 'center',
            background: '#f8fafc',
            borderRadius: '12px',
            border: '1px dashed #cbd5e1',
            color: '#64748b'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '6px' }}>🕒</div>
            <p style={{ margin: '0 0 4px 0', fontSize: '13.5px', fontWeight: 700, color: '#334155' }}>
              No slots available on {activeDateItem.formattedDate || selectedDateStr}
            </p>
            <p style={{ margin: 0, fontSize: '11.5px', color: '#64748b' }}>
              Please select another date from the carousel above to view available session times.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlotAvailabilityPicker;
